import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";

import EntityVersionAnalyticsSheet, {
  OpenAnalyticsButton,
  snapshotStructuredSearchQuery,
  type EntityVersionAnalyticsQuerySnapshot,
} from "@/components/EntityVersionAnalyticsSheet";
import LoadingSpinner from "@/components/LoadingSpinner";
import { StatsShimmer } from "@/components/shared/StatsShimmer";
import { StructuredSearchbar, useStructuredSearchStore } from "@/components/StructuredSearch";
import { WORKFLOW_ANALYTICS_URL_NAMESPACE } from "@/components/StructuredSearch/hooks/structuredSearchUrlNamespaces";
import { useStructuredSearchURLSync } from "@/components/StructuredSearch/hooks/useStructuredSearchURLSync";
import TracesAgGrid from "@/components/TracesSearch/TracesAgGrid";
import {
  TracesTableRefreshProvider,
  useTracesTableRefresh,
} from "@/components/TracesSearch/TracesAgGrid/traces-table-refresh-context";
import { TimezoneProvider } from "@/context/timezone-context";
import { useUser } from "@/context/user-context";
import { useStatBoxes } from "@/hooks/useStatsBoxes";
import { useRequestWorkflowVersionStats } from "@/queries/workflows/useRequestWorkflowVersionStats";
import { useApplyOlderDataConstraints } from "@/routes/request-logs/search/hooks/useApplyOlderDataConstraints";

interface WorkflowVersionAnalyticsProps {
  workflowId: string;
  versionNumber: string;
  versionCreatedAt?: string;
}

const WorkflowVersionAnalyticsContent = observer(
  ({
    workflowId,
    versionNumber,
    versionCreatedAt,
  }: {
    workflowId: string;
    versionNumber: string;
    versionCreatedAt?: string;
  }) => {
    const structuredSearchStore = useStructuredSearchStore();
    const { activeWorkspace } = useUser();
    const tableRefresh = useTracesTableRefresh();
    const minDate = versionCreatedAt ? new Date(versionCreatedAt) : undefined;
    const [isAnalyticsSheetOpen, setIsAnalyticsSheetOpen] = useState(false);
    const [analyticsQuerySnapshot, setAnalyticsQuerySnapshot] =
      useState<EntityVersionAnalyticsQuerySnapshot | null>(null);
    const [analyticsSheetKey, setAnalyticsSheetKey] = useState(0);
    const workflowInputVariableScope = useMemo(
      () => ({ workflowId, workflowVersionNumber: versionNumber }),
      [versionNumber, workflowId],
    );

    useStructuredSearchURLSync(
      structuredSearchStore,
      structuredSearchStore.urlHydrationReady,
      WORKFLOW_ANALYTICS_URL_NAMESPACE,
    );
    useApplyOlderDataConstraints();

    // Version-wide totals — independent of the table's date range / filters.
    const statsQuery = useRequestWorkflowVersionStats(workflowId, versionNumber);
    const statsBoxes = useStatBoxes(statsQuery.data?.stats);
    const isLoadingStats =
      statsQuery.isLoading || statsQuery.isFetching || !statsQuery.data?.success;
    const statsComponent = isLoadingStats ? <StatsShimmer /> : statsBoxes;

    const handleOpenAnalytics = useCallback(() => {
      setAnalyticsQuerySnapshot(snapshotStructuredSearchQuery(structuredSearchStore));
      setAnalyticsSheetKey(key => key + 1);
      setIsAnalyticsSheetOpen(true);
    }, [structuredSearchStore]);

    return (
      <div className="flex h-full flex-col gap-y-2 overflow-hidden pt-2">
        {statsComponent}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex-shrink-0 overflow-visible py-2">
            <StructuredSearchbar
              className="w-full"
              title="Filters"
              minDate={minDate}
              onRefresh={() => tableRefresh.triggerRefresh()}
              isRefreshing={tableRefresh.isTableLoading}
              headerActions={
                <OpenAnalyticsButton
                  onClick={handleOpenAnalytics}
                  cutoverDate={activeWorkspace?.traces_search_cutover_date}
                />
              }
            />
          </div>
          {structuredSearchStore.urlHydrationReady ? (
            <TracesAgGrid workflowInputVariableScope={workflowInputVariableScope} />
          ) : (
            <div className="flex h-full items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
        </div>
        <EntityVersionAnalyticsSheet
          open={isAnalyticsSheetOpen}
          onOpenChange={setIsAnalyticsSheetOpen}
          querySnapshot={analyticsQuerySnapshot}
          variant="traces"
          contentKey={analyticsSheetKey}
        />
      </div>
    );
  },
);

const WorkflowVersionAnalytics = ({
  workflowId,
  versionNumber,
  versionCreatedAt,
}: WorkflowVersionAnalyticsProps) => (
  <TimezoneProvider>
    <TracesTableRefreshProvider>
      <WorkflowVersionAnalyticsContent
        workflowId={workflowId}
        versionNumber={versionNumber}
        versionCreatedAt={versionCreatedAt}
      />
    </TracesTableRefreshProvider>
  </TimezoneProvider>
);

export default WorkflowVersionAnalytics;
